/**
 * Minimal toast context for the admin section.
 *
 * Pages call `useToast().push({ type, message })` to surface success/failure
 * notifications. We wrap Meridian's Alert inside a Toaster so every admin
 * page gets consistent styling without duplicating boilerplate.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import Toaster from '@amzn/meridian/toaster';
import Alert from '@amzn/meridian/alert';

export type ToastType = 'success' | 'error' | 'warning' | 'neutral';

export interface ToastPayload {
  type?: ToastType;
  message: string;
  title?: string;
  durationMs?: number;
}

interface ToastRecord extends ToastPayload {
  id: string;
  onClose: () => void;
}

interface ToastContextValue {
  push: (toast: ToastPayload) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

let seq = 0;
const nextId = () => {
  seq += 1;
  return `admin-toast-${Date.now()}-${seq}`;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const remove = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (payload: ToastPayload) => {
      const id = nextId();
      const record: ToastRecord = {
        ...payload,
        id,
        onClose: () => remove(id),
      };
      setToasts((prev) => [...prev, record]);
    },
    [remove],
  );

  const value = useMemo<ToastContextValue>(() => ({ push }), [push]);

  const onCloseToast = useCallback(
    (toast: ToastRecord, _toastIndex: number) => {
      // Meridian's ``Toaster`` calls this when a toast is auto-dismissed
      // (e.g. on a timeout). Previously this was a no-op, so
      // auto-dismissed toasts never left the ``toasts`` state array and
      // accumulated indefinitely. The per-toast manual-close button goes
      // through ``record.onClose``, which also calls ``remove(id)``; the
      // dedup inside ``remove`` makes calling it from both paths safe.
      remove(toast.id);
    },
    [remove],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster toasts={toasts} onCloseToast={onCloseToast}>
        {(toast: ToastRecord) => (
          <Alert toast={true} type={toast.type || 'success'} title={toast.title} onClose={toast.onClose}>
            {toast.message}
          </Alert>
        )}
      </Toaster>
    </ToastContext.Provider>
  );
};

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}
